/**
 * Neues Konto anlegen – eigenständige Tab-Seite (Buchhaltung)
 */
import { useState } from 'react';
import { Landmark, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTabs } from '@/contexts/TabContext';
import { emitEntityCreated, ENTITY_TYPES } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function NeuKontoPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [form, setForm] = useState({
    bezeichnung: 'Girokonto', bank: '', iban: '', typ: 'girokonto', rechnungslegung_pflicht: true
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.bank) { toast.error('Bitte Bank angeben'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/konten`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, klient_id: params.klientId })
      }).then(r => r.json());
      toast.success('Konto angelegt');
      emitEntityCreated(ENTITY_TYPES.KONTO, res);
      closeTab(activeTab.id);
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="neu-konto-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Landmark className="w-5 h-5 text-blue-600" />Neues Konto anlegen
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>Fügen Sie ein Bankkonto für den Betreuten hinzu</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Bezeichnung</Label>
              <Select value={form.typ} onValueChange={v => setForm(p => ({ ...p, typ: v, bezeichnung: v === 'girokonto' ? 'Girokonto' : v === 'sparkonto' ? 'Sparkonto' : 'Depot' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="girokonto">Girokonto</SelectItem>
                  <SelectItem value="sparkonto">Sparkonto</SelectItem>
                  <SelectItem value="depot">Depot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank</Label>
              <Input value={form.bank} onChange={e => setForm(p => ({ ...p, bank: e.target.value }))} placeholder="z.B. Sparkasse" data-testid="konto-bank-input" />
            </div>
          </div>
          <div>
            <Label>IBAN</Label>
            <Input value={form.iban} onChange={e => setForm(p => ({ ...p, iban: e.target.value }))} placeholder="DE..." className="font-mono" data-testid="konto-iban-input" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.rechnungslegung_pflicht} onChange={e => setForm(p => ({ ...p, rechnungslegung_pflicht: e.target.checked }))} id="rl" className="rounded" />
            <Label htmlFor="rl" className="text-sm cursor-pointer">Rechnungslegung-Pflicht</Label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-konto">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Check className="w-4 h-4 mr-2" />Konto anlegen</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
